import { Stack } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React from 'react'
import { EVENT } from '../../../../constants'
import {
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../models/fulfilment'
import { LineItem } from '../../../../models/line-item'
import { useSaleInventoryData } from '../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import { selectIsEcomOrder } from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  openFulfilmentLineItemPopover,
  setLineItemFulfilmentNoteError,
} from '../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { FulfilmentSelect } from '../../../react/modals/mark-as-unfulfilled-modal/Steps/Step1/FulfillmentSelect'
import { NoteError } from '../../../shared/NoteInput'
import { LineItemFulfilmentAddress } from './LineItemFulfilmentAddress'
import { LineItemFulfilmentNote } from './LineItemFulfilmentNote'
import { OutdatedTaxCategoryWarning } from './OutdatedTaxCategoryWarning'
import { useAutoPopulateFulfillmentAddresses } from './hook/useAutoPopulateFulfillmentAddresses'
import { useSaleFulfilment } from './hook/useSaleFulfilment'

export const LineItemDisplayFulfilment: React.FC<{
  lineItem: LineItem
  onSelectFulfilmentType?: (type: FulfilmentType) => void
}> = ({ lineItem: defaultLineItem, onSelectFulfilmentType }) => {
  const t = useT()

  const saleInventoryData = useSaleInventoryData()
  const dispatch = useAppDispatch()

  const {
    currentLineItem,
    fulfilmentDetail,
    setFulfilmentType,
    setFulfilmentNote,
    isDisabled,
  } = useSaleFulfilment(defaultLineItem.localId)

  // Auto populate fulfillment addresses based on customer addresses
  useAutoPopulateFulfillmentAddresses(fulfilmentDetail)

  const isEcomOrder = useAppSelector(selectIsEcomOrder)

  const handleNoteErrorsChange = (errors: NoteError[]) => {
    if (errors.length === 0) {
      setLineItemFulfilmentNoteError({
        id: currentLineItem.localId,
        error: null,
      })

      return
    }

    setLineItemFulfilmentNoteError({
      id: currentLineItem.localId,
      error: errors[0],
    })
  }

  const shouldShowFulfilmentAddress =
    currentLineItem?.fulfilmentType === FulfilmentType.DELIVERY && !isEcomOrder

  const ffSpecialOrder = getFeatureFromCache('inventory_special_orders')

  return (
    <Stack space={4}>
      <OutdatedTaxCategoryWarning lineItem={defaultLineItem} />
      <FulfilmentSelect
        showUnspecified
        disabled={isDisabled}
        onTypeChange={type => {
          setFulfilmentType(type, fulfilmentDetail?.note || '')
          if (onSelectFulfilmentType) {
            onSelectFulfilmentType(type)
          }
          if (!isDefinedFulfilmentType(type)) {
            saleInventoryData.enableInventoryValidation(currentLineItem)
            if (
              Number(currentLineItem.quantity) > 0 &&
              saleInventoryData.isInventoryTracked(currentLineItem) &&
              !saleInventoryData.hasInventory(currentLineItem)
            ) {
              dispatch(openFulfilmentLineItemPopover(currentLineItem.localId))
            }
          } else {
            saleInventoryData.disableInventoryValidation(currentLineItem)
          }
          saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
        }}
        defaultType={currentLineItem?.fulfilmentType}
        stretched
        message={t(
          'You can select one method per sale. To set another method, create another sale.',
          {
            _context: 'Note explaining how fulfillment methods work',
          }
        )}
        outOfStockMessage={
          ffSpecialOrder &&
          isDefinedFulfilmentType(currentLineItem?.fulfilmentType)
            ? t(
                'This product is out of stock. If incoming stock is insufficient, it will be available for special order and ready to be fulfilled upon restock.',
                {
                  _context: 'Special order product info message',
                }
              )
            : undefined
        }
      />

      {shouldShowFulfilmentAddress && (
        <LineItemFulfilmentAddress
          fulfillmentDetail={fulfilmentDetail}
          lineItemLocalId={defaultLineItem.localId}
          isModalOpeningOnError={fulfilmentDetail?.shouldAddNewAddress}
        />
      )}

      {!!currentLineItem?.fulfilmentType &&
        currentLineItem.fulfilmentType !== FulfilmentType.UNSPECIFIED && (
          <LineItemFulfilmentNote
            note={fulfilmentDetail?.note || ''}
            isDisabled={isDisabled}
            onChange={setFulfilmentNote}
            onErrorsChange={handleNoteErrorsChange}
          />
        )}
    </Stack>
  )
}
